/**
 * Pure helpers for the Station Operator chat client: SSE stream consumption and
 * assistant deep-link parsing. Kept out of ChatPanel.tsx so that file only
 * exports a component (react-refresh/only-export-components) and so these can be
 * unit-tested in isolation.
 */

export interface ChatLink {
  label: string;
  path: string;
}

/** Canonical route prefixes the bot is allowed to deep-link into. */
const ALLOWED_PATH = /^\/(station|search|observations|availability|network|quality)(\/|\?|$)/;
/** Legacy redirect shape to reject: /observations/:code (canonical is /station/:code/observations). */
const LEGACY_OBSERVATIONS = /^\/observations\/[^?]/;

/**
 * Extract a trailing ```links fenced block (JSON array of {label,path}) from an
 * assistant message. Returns the cleaned text (block removed) and the validated
 * links. Only canonical, same-origin relative paths survive.
 */
export function parseLinks(text: string): { text: string; links: ChatLink[] } {
  const fence = /```links\s*([\s\S]*?)```/i;
  const match = text.match(fence);
  if (!match) return { text, links: [] };
  let links: ChatLink[] = [];
  try {
    const parsed = JSON.parse(match[1].trim());
    if (Array.isArray(parsed)) {
      links = parsed
        .filter(
          (l) =>
            l &&
            typeof l.label === 'string' &&
            typeof l.path === 'string' &&
            l.path.startsWith('/') &&
            !l.path.startsWith('//') && // reject protocol-relative //evil.com
            ALLOWED_PATH.test(l.path) &&
            !LEGACY_OBSERVATIONS.test(l.path) // reject legacy /observations/:code (301s)
        )
        .map((l) => ({ label: l.label, path: l.path }));
    }
  } catch {
    /* malformed block — drop it */
  }
  const cleaned = text.replace(fence, '').trim();
  return { text: cleaned, links };
}

/**
 * Extract a trailing ```followups fenced block (JSON array of short prompt
 * strings NORA suggests the user might ask next) and strip it from the text.
 * Each suggestion is a plain string that gets re-sent verbatim on click, so we
 * bound count/length and drop anything non-string.
 */
export function parseFollowups(text: string): { text: string; followups: string[] } {
  const fence = /```followups\s*([\s\S]*?)```/i;
  const match = text.match(fence);
  if (!match) return { text, followups: [] };
  let followups: string[] = [];
  try {
    const parsed = JSON.parse(match[1].trim());
    if (Array.isArray(parsed)) {
      followups = parsed
        .filter((f) => typeof f === 'string' && f.trim().length > 0 && f.length <= 120)
        .map((f) => f.trim())
        .slice(0, 4);
    }
  } catch {
    /* malformed block — drop it */
  }
  const cleaned = text.replace(fence, '').trim();
  return { text: cleaned, followups };
}

export interface SseHandlers {
  onToken: (text: string) => void;
  onToolStart: (name: string) => void;
  onToolEnd: (name: string) => void;
  onError: (message: string) => void;
}

/**
 * Read an SSE stream (event:/data: pairs separated by blank lines) from a
 * ReadableStream and dispatch to handlers.
 */
export async function consumeSse(body: ReadableStream<Uint8Array>, handlers: SseHandlers): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    // Process complete event blocks (terminated by a blank line).
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      dispatchSseBlock(block, handlers);
    }
  }
  if (buffer.trim()) dispatchSseBlock(buffer, handlers);
}

function dispatchSseBlock(block: string, handlers: SseHandlers): void {
  const evMatch = block.match(/^event: (.+)$/m);
  const dataMatch = block.match(/^data: (.+)$/m);
  if (!evMatch || !dataMatch) return;
  const event = evMatch[1].trim();
  let data: Record<string, unknown> = {};
  try {
    data = JSON.parse(dataMatch[1]);
  } catch {
    return;
  }
  switch (event) {
    case 'token':
      handlers.onToken(String(data.text ?? ''));
      break;
    case 'tool_start':
      handlers.onToolStart(String(data.name ?? ''));
      break;
    case 'tool_end':
      handlers.onToolEnd(String(data.name ?? ''));
      break;
    case 'error':
      handlers.onError(String(data.message ?? 'error'));
      break;
    // thinking / message_done / done need no UI action here
    default:
      break;
  }
}

/**
 * Human-readable status label for a NORA `tool_start` event. Shared by the chat
 * panel and the inline QC verdict panel so the tool vocabulary stays in one place.
 */
export function chipLabel(toolName: string): string {
  const map: Record<string, string> = {
    find_station: 'Looking up the station…',
    resolve_station: 'Resolving the identifier…',
    nearest_stations: 'Finding the nearest stations…',
    list_stations: 'Searching stations…',
    list_locations: 'Listing locations…',
    fleet_stats: 'Reading fleet statistics…',
    station_coverage: 'Checking coverage…',
    station_qc: 'Checking quality control…',
    station_status: 'Checking status…',
    network_health: 'Checking network health…',
    latest_observation: 'Fetching latest observations…',
    observation_window: 'Reviewing the observation history…',
    forecast_comparison: 'Comparing against the OneFX forecast…',
    climatology_stats: 'Checking the climatological normal…',
    probabilistic_envelope: 'Checking the probabilistic envelope…',
    raster_cross_check: 'Cross-checking against weather radar/satellite…',
  };
  return map[toolName] || `Running ${toolName}…`;
}
